import Grid from '@app/components/Grid';
import { Card, Colors, FormGroup, InputGroup, MenuItem } from '@blueprintjs/core';
import { AiImageCategory } from 'dy-frontend-http-repository/lib/modules/AiImage/enums';
import React, { useEffect, useState } from 'react';
import PromptOverview from '../../PromptOverview';
import { configuration } from './consts';
import { ThreeDimensionalArtworkCategoryPromptKeys } from './types';
import Select from '@components/Select';

interface ThreeDimensionalArtworkGeneratorProps {
    isGenerating: boolean;
    onPromptChange: (prompt: string | null) => void;
}

type Props = ThreeDimensionalArtworkGeneratorProps;

const ThreeDimensionalArtworkGenerator: React.FC<Props> = ({ isGenerating, onPromptChange }) => {
    const [focusedInputKey, setFocusedInputKey] = useState<string | null>(null);
    const [subject, setSubject] = useState('');
    const [texture, setTexture] = useState(
        configuration.inputs['texture'] ? configuration.inputs['texture'].initialValue : ''
    );
    const [lighting, setLighting] = useState(
        configuration.inputs['lighting'] ? configuration.inputs['lighting'].initialValue : ''
    );
    const [color, setColor] = useState(configuration.inputs['color'] ? configuration.inputs['color'].initialValue : '');

    const getCategoryPromptKeys = (): ThreeDimensionalArtworkCategoryPromptKeys => {
        const overviewRendererProps: ThreeDimensionalArtworkCategoryPromptKeys = {
            subject: { isActive: false, key: '', text: '' },
            texture: { isActive: false, key: '', text: '' },
            lighting: { isActive: false, key: '', text: '' },
            color: { isActive: false, key: '', text: '' },
        };

        for (const inputKey of Object.keys(configuration.inputs)) {
            // Prompt property not used in overview
            if (!configuration.inputs[inputKey].isUsedInPromptOverview) {
                continue;
            }

            const value = promptPropertiesData[inputKey].value;
            const elementKey = `${AiImageCategory.THREE_DIMENSIONAL}_${inputKey}`;

            // No input is focused
            if (focusedInputKey === null) {
                overviewRendererProps[inputKey] = {
                    key: elementKey,
                    text: value,
                    isActive: false,
                };
                continue;
            }

            // Input for property "key" is focused
            if (focusedInputKey === inputKey) {
                overviewRendererProps[inputKey] = {
                    key: elementKey,
                    text: value,
                    isActive: true,
                };
                continue;
            }

            overviewRendererProps[inputKey] = {
                key: elementKey,
                text: value,
                isActive: false,
            };
        }

        return overviewRendererProps;
    };

    useEffect(() => {
        if (subject.trim().length === 0) {
            onPromptChange(null);
            return;
        }

        onPromptChange(configuration.getFinalPrompt(getCategoryPromptKeys()));
    }, [subject, texture, lighting, color]);

    const promptPropertiesData: {
        [key in string]: { value: string; dispatch: React.Dispatch<React.SetStateAction<string>> };
    } = {
        subject: { value: subject, dispatch: setSubject },
        texture: { value: texture, dispatch: setTexture },
        lighting: { value: lighting, dispatch: setLighting },
        color: { value: color, dispatch: setColor },
    };

    const renderPrompt = () => {
        return (
            <Card compact style={{ background: Colors.DARK_GRAY2 }}>
                <PromptOverview
                    template={configuration.promptTemplate}
                    categoryPromptConfiguration={getCategoryPromptKeys()}
                />
            </Card>
        );
    };

    const renderGeneratorFields = () => {
        const filterItemOption = (query: string, item: string) => {
            return item.toLowerCase().indexOf(query.toLowerCase()) >= 0;
        };

        const renderCreateItemOption = (
            query: string,
            active: boolean,
            handleClick: React.MouseEventHandler<HTMLElement>
        ) => {
            return (
                <MenuItem
                    icon="add"
                    text={`${query}`}
                    active={active}
                    onClick={handleClick}
                    shouldDismissPopover={false}
                />
            );
        };

        return (
            <Grid container>
                {Object.entries(configuration.inputs).map(([key, promptPropertyInputConfiguration]) => {
                    if (promptPropertyInputConfiguration.inputType === 'input') {
                        return (
                            <Grid lg={6}>
                                <FormGroup label={promptPropertyInputConfiguration.label}>
                                    <InputGroup
                                        placeholder={promptPropertyInputConfiguration.placeholder}
                                        value={promptPropertiesData[key].value}
                                        onChange={(e) => promptPropertiesData[key].dispatch(e.target.value)}
                                        onFocus={() => setFocusedInputKey(key)}
                                    />
                                </FormGroup>
                            </Grid>
                        );
                    } else {
                        let items: string[] = promptPropertyInputConfiguration.defaultOptions;
                        const canAddCustomOption = promptPropertyInputConfiguration.isCustomOptionAllowed;

                        const renderItemOption = (item: string, { handleClick }) => {
                            return (
                                <MenuItem
                                    key={item}
                                    text={item}
                                    onClick={handleClick}
                                    active={item === promptPropertiesData[key].value}
                                />
                            );
                        };

                        return (
                            <Grid lg={6}>
                                <FormGroup label={promptPropertyInputConfiguration.label}>
                                    <Select<string>
                                        fill
                                        filterable
                                        key={key}
                                        items={items}
                                        disabled={isGenerating}
                                        itemPredicate={filterItemOption}
                                        createNewItemFromQuery={canAddCustomOption ? (query) => query : undefined}
                                        createNewItemRenderer={canAddCustomOption ? renderCreateItemOption : undefined}
                                        itemRenderer={renderItemOption}
                                        onItemSelect={(item) => promptPropertiesData[key].dispatch(item)}
                                        popoverProps={{
                                            onOpening: () => setFocusedInputKey(key),
                                        }}
                                        inputProps={{
                                            placeholder: 'Add keywords / Search',
                                        }}
                                        selectButtonProps={{
                                            fill: true,
                                            text: promptPropertiesData[key].value,
                                            rightIcon: 'double-caret-vertical',
                                        }}
                                    />
                                </FormGroup>
                            </Grid>
                        );
                    }
                })}
            </Grid>
        );
    };

    return (
        <div>
            <div className="mb-2">{renderPrompt()}</div>
            <div>{renderGeneratorFields()}</div>
        </div>
    );
};

export default ThreeDimensionalArtworkGenerator;
